import { Alert, Box, Button, Typography } from "@mui/material";
import { AffectGrid } from "./questionnaireComponents/AffectGrid";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { putDairyAnswerData } from "./utility/putDairyAnswerData";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { DummyQuestion } from "./questionnaireComponents/DummyQuestion";
import { EventValueQuestion } from "./questionnaireComponents/EventValueQuestion";
import { EventControlQuestion } from "./questionnaireComponents/EventControlQuestion";

import { motion, AnimatePresence } from "framer-motion";
import { EventRelationQuestion } from "./questionnaireComponents/EventRelationQuestion";
import { useAuth } from "../context/AuthContext";
import { getChatManualConfirmStatus } from "./utility/getChatManualConfirmStatus";
import { userInfo } from "os";
import {
  defaultPanasAnswer,
  Panas,
  panasAnsewerType,
} from "./questionnaireComponents/Panas";
import {
  defaultGodSpeedAnswer,
  GodSpeed,
  godSpeedType,
} from "./utility/GodSpeed";
import { putFirstDayAnswer } from "./utility/putPanasAnswer";
import { getFirstDayAnswerById } from "./utility/getPanasData";

type affectValueType = {
  pleasantness: number;
  arousal: number;
};

let maxPageNumber = 0;

const variants = {
  enter: (dir: "left" | "right") => ({
    x: dir === "right" ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { x: "0%", opacity: 1 },
  exit: (dir: "left" | "right") => ({
    x: dir === "right" ? "-100%" : "100%",
    opacity: 0,
  }),
};

// 項目が変わらない場合はPOSTと共通化する
export const PreQuestionnairePage = () => {
  const navigate = useNavigate();
  const { idToken, refreshAuthSession } = useAuth();
  const [affectValue, setAffectValue] = useState<affectValueType | null>(null);
  const [dummyValue, setDummyValue] = useState<number | null>(null);
  const [eventValue, setEventValue] = useState<number | null>(null);
  const [eventControllable, setEventControllable] = useState<boolean | null>(
    null
  );
  const [eventRelation, setEventRelation] = useState<boolean | null>(null);
  const [panasAnswer, setPanasAnswer] =
    useState<panasAnsewerType>(defaultPanasAnswer);

  const [includePanas, setIncludePanas] = useState<boolean | null>(null);

  const [godSpeedAnswer, setGodSpeedAnswer] = useState<godSpeedType>(
    defaultGodSpeedAnswer
  );

  const location = useLocation();
  const state = location.state as {
    userId?: string;
    date?: string;
  };
  const [questionnairePage, setQuestionnairePage] = useState<number>(1);

  const directionRef = useRef<"left" | "right">("right");

  const isAnswerEnable =
    affectValue &&
    eventValue &&
    eventControllable !== null &&
    eventRelation !== null &&
    (includePanas ? isAllNotNull(panasAnswer) : true);

  const alertMessageIsOn =
    (!includePanas && !isAnswerEnable && affectValue !== null) ||
    (includePanas && !isAnswerEnable && isAllNotNull(panasAnswer));

  // PANASの回答が完了しているかどうかを判定する関数
  function isAllNotNull(obj: {
    [key: string]: number | null;
  }): obj is { [key: string]: number } {
    return Object.values(obj).every((v): v is number => v !== null);
  }

  const answerButtonClicked = () => {
    // アンケートデータ保存処理
    if (!state.userId) return;
    if (!isAnswerEnable) return;
    if (!idToken) return;

    const data = {
      affectGrid: affectValue,
      eventValue: eventValue,
      eventControllable: eventControllable,
      eventRelation: eventRelation,
    };
    const dateForAnswer = state.date + "-pre";
    putDairyAnswerData(state.userId, dateForAnswer, data, idToken).then(
      (response) => {
        console.log(response);
      }
    );
    if (includePanas) {
      putFirstDayAnswer(
        state.userId,
        dateForAnswer,
        { panas: panasAnswer },
        idToken
      ).then((response) => {
        console.log(response);
      });
    }

    // 新規の会話データ作成処理は入れない
    // チャットページで会話IDを受け取らなかった場合に作成する

    // チャットページへ遷移 チャットマニュアルを表示するかどうかを確認する 会話IDを渡さずに遷移する
    getChatManualConfirmStatus(state.userId, idToken).then((data) => {
      if (data.chatManualConfirm) {
        // 確認が完了している場合 チャットページへ遷移 会話IDは渡さない
        navigate("/chat", {
          state: { complete: false, conversationDate: state.date },
        });
      } else {
        // 確認が完了していない場合はマニュアルへ遷移
        navigate("/chat-manual", {
          state: { date: state.date },
        });
      }
    });
  };

  const switchTab = (next: number) => {
    const dir = next > questionnairePage ? "right" : "left";
    directionRef.current = dir;
    setQuestionnairePage(next);
  };

  // 最初の読み込み PANASの回答が済んでているかどうかでアンケート内容が変わる
  useEffect(() => {
    if (idToken && state.userId) {
      getFirstDayAnswerById(state.userId, idToken).then((data) => {
        // データが無い場合はダミーデータ[]が返ってくる
        if (data.length === 0) {
          maxPageNumber = 5;
          setIncludePanas(true);
        } else {
          maxPageNumber = 4;
          setIncludePanas(false);
        }
      });
    }
  }, []);

  // const testFunc = () => {
  //   console.log("test");
  //   if (!state.userId) return;
  //   if (!idToken) return;

  //   console.log("test2");

  //   const dateForAnswer = state.date + "-pre";
  //   putPanasData(state.userId, dateForAnswer, panasAnswer, idToken).then(
  //     (response) => {
  //       console.log(response);
  //     }
  //   );
  // };

  if (includePanas === null) return null;

  return (
    <>
      <Box sx={{ minHeight: "60%" }}>
        <Box sx={{ textAlign: "center", marginBottom: "1rem" }}>
          <Typography variant="h6">
            事前アンケート({questionnairePage}/{maxPageNumber})
          </Typography>
          <Typography variant="caption">
            チャット前のアンケートにご回答ください。
          </Typography>
        </Box>

        {/* <Button
          variant="contained"
          color="error"
          onClick={() => setQuestionnairePage(10)}
        >
          TEST
        </Button> */}

        <Box sx={pageButtonContainerStyle}>
          <Button
            onClick={() => switchTab(questionnairePage - 1)}
            disabled={questionnairePage === 1}
          >
            <ArrowBackIosIcon />
            前の設問へ
          </Button>
          <Button
            onClick={() => switchTab(questionnairePage + 1)}
            disabled={questionnairePage === maxPageNumber}
          >
            次の設問へ
            <ArrowForwardIosIcon />
          </Button>
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={questionnairePage}
            custom={directionRef.current}
            variants={variants}
            initial="enter"
            animate="center"
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              bounce: 0, // ← ここを追加
              duration: 0.5,
            }}
          >
            {questionnairePage === 1 && (
              <EventValueQuestion
                eventValue={eventValue}
                setEventValue={setEventValue}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}
            {questionnairePage === 2 && (
              <EventControlQuestion
                eventControllable={eventControllable}
                setEventControllable={setEventControllable}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}

            {questionnairePage === 3 && (
              <EventRelationQuestion
                eventRelation={eventRelation}
                setEventRelation={setEventRelation}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}

            {questionnairePage === 4 && (
              <AffectGrid
                affectValue={affectValue}
                setAffectValue={setAffectValue}
                isAutoPageTurn={includePanas}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
                widthValue={window.innerWidth}
              />
            )}

            {questionnairePage === 5 && (
              <Panas
                panasAnswer={panasAnswer}
                setPanasAnswer={setPanasAnswer}
              />
            )}

            {questionnairePage === 100 && (
              <DummyQuestion value={dummyValue} setValue={setDummyValue} />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      <Box sx={{ textAlign: "center", marginTop: "3rem" }}>
        <Button
          variant="contained"
          disabled={!isAnswerEnable}
          style={{ width: "fit-content" }}
          onClick={answerButtonClicked}
        >
          回答する
        </Button>
        {alertMessageIsOn && (
          <Typography sx={{ marginTop: "0.2rem", color: "#C0392B" }}>
            未回答の設問があります！
          </Typography>
        )}
        <div style={{ height: "1rem" }} />
        {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
          {alertMessageIsOn && (
            <Alert sx={{ marginTop: 1, width: "80%" }} severity="warning">
              未回答の設問があります！
            </Alert>
          )}
        </Box> */}
        {/* <Button onClick={testFunc}>TEST</Button> */}
      </Box>
    </>
  );
};

// css
const pageButtonContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "0.5rem 1rem",
};
